import React, { useState, useEffect } from 'react';
import KenKenGrid from './components/KenKenGrid';
import CageModal from './components/CageModal';
import { solveKenKen, getHint } from './utils/kenkenSolver';

interface Cell {
  row: number;
  col: number;
}

interface Cage {
  id: string;
  cells: Cell[];
  target: number;
  operation: string;
}

function App() {
  const [gridSize, setGridSize] = useState<number>(4); // Default to 4x4 for easier testing
  const [selectedCells, setSelectedCells] = useState<Cell[]>([]);
  const [cages, setCages] = useState<Cage[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [solution, setSolution] = useState<number[][] | null>(null);

  // History for undo/redo
  const [history, setHistory] = useState<Cage[][]>([[]]);
  const [historyPointer, setHistoryPointer] = useState<number>(0);

  useEffect(() => {
    if (selectedCells.length > 0) {
      setShowModal(true);
    }
  }, [selectedCells]);

  const handleGridSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGridSize(Number(event.target.value));
    setSelectedCells([]);
    setCages([]);
    setSolution(null);
    setHistory([[]]); // Reset history
    setHistoryPointer(0);
  };

  const handleCellSelection = (cells: Cell[]) => {
    setSelectedCells(cells);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCells([]);
  };

  const handleSaveCage = (target: number, operation: string, cells: Cell[]) => {
    const newCage: Cage = {
      id: `cage-${cages.length + 1}`,
      cells,
      target,
      operation,
    };
    const newCages = [...cages, newCage];
    setCages(newCages);

    // Update history
    const newHistory = history.slice(0, historyPointer + 1);
    setHistory([...newHistory, newCages]);
    setHistoryPointer(newHistory.length);

    handleCloseModal();
  };

  const handleSolve = () => {
    const solvedBoard = solveKenKen(gridSize, cages);
    setSolution(solvedBoard);
  };

  const handleHint = () => {
    // Create a board from the current solution (if available) or an empty board
    const currentBoard = solution ? solution.map(row => [...row]) : Array.from({ length: gridSize }, () => Array(gridSize).fill(0));
    const hint = getHint(gridSize, cages, currentBoard);

    if (hint) {
      const newSolution = solution ? solution.map(row => [...row]) : Array.from({ length: gridSize }, () => Array(gridSize).fill(0));
      newSolution[hint.row][hint.col] = hint.value;
      setSolution(newSolution);
    } else {
      alert('No hint available or puzzle is already solved!');
    }
  };

  const handleUndo = () => {
    if (historyPointer > 0) {
      const prevPointer = historyPointer - 1;
      setCages(history[prevPointer]);
      setHistoryPointer(prevPointer);
      setSolution(null); // Clear solution on undo
    }
  };

  const handleRedo = () => {
    if (historyPointer < history.length - 1) {
      const nextPointer = historyPointer + 1;
      setCages(history[nextPointer]);
      setHistoryPointer(nextPointer);
      setSolution(null); // Clear solution on redo
    }
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center mb-4">KenKen Puzzle Editor & Solver</h1>
      <div className="row">
        <div className="col-md-8 kenken-grid-container">
          <KenKenGrid
            size={gridSize}
            onCellSelection={handleCellSelection}
            selectedCells={selectedCells}
            cages={cages}
            solution={solution}
          />
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              Controls
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label htmlFor="gridSize" className="form-label">Grid Size:</label>
                <select
                  id="gridSize"
                  className="form-select"
                  value={gridSize}
                  onChange={handleGridSizeChange}
                >
                  {[3, 4, 5, 6, 7, 8, 9].map((size) => (
                    <option key={size} value={size}>
                      {size}x{size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={handleUndo}
                  disabled={historyPointer === 0}
                >
                  Undo
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleRedo}
                  disabled={historyPointer === history.length - 1}
                >
                  Redo
                </button>
              </div>
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-success w-100 me-2"
                  onClick={handleSolve}
                  disabled={cages.length === 0}
                >
                  Solve
                </button>
                <button
                  className="btn btn-info w-100"
                  onClick={handleHint}
                  disabled={cages.length === 0}
                >
                  Hint
                </button>
              </div>
              {/* Display created cages */}
              <div className="mt-4">
                <h5>Cages:</h5>
                {cages.length === 0 ? (
                  <p>No cages created yet.</p>
                ) : (
                  <ul className="list-group">
                    {cages.map((cage) => (
                      <li key={cage.id} className="list-group-item">
                        Target: {cage.target}, Operation: {cage.operation}, Cells: {cage.cells.length}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CageModal
        show={showModal}
        onHide={handleCloseModal}
        selectedCells={selectedCells}
        onSave={handleSaveCage}
      />
    </div>
  );
}

export default App;
